import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AlertTriangle, Info, Loader2 } from "lucide-react";

/**
 * Global dialog service. Provides imperative confirm/prompt/alert through context.
 *
 * Usage:
 *   const dialog = useDialog();
 *   if (await dialog.confirm({ title: "...", confirmLabel: "Delete" })) { ... }
 *   const name = await dialog.prompt({ title: "Section name" });
 *   await dialog.alert({ title: "Not allowed" });
 */

const DialogContext = createContext(null);

export function DialogProvider({ children }) {
  const [stack, setStack] = useState([]); // array of dialog descriptors with resolve fn
  const idRef = useRef(0);

  const open = useCallback((descriptor) => {
    return new Promise((resolve) => {
      idRef.current += 1;
      const id = idRef.current;
      setStack((s) => [...s, { ...descriptor, id, resolve }]);
    });
  }, []);

  const close = useCallback((id, value) => {
    setStack((s) => {
      const found = s.find((x) => x.id === id);
      if (found) found.resolve(value);
      return s.filter((x) => x.id !== id);
    });
  }, []);

  const api = {
    confirm: (opts) => open({ kind: "confirm", ...opts }),
    prompt: (opts) => open({ kind: "prompt", ...opts }),
    alert: (opts) => open({ kind: "alert", ...opts }),
    choose: (opts) => open({ kind: "choose", ...opts }),
    actions: (opts) => open({ kind: "actions", ...opts }),
  };

  return (
    <DialogContext.Provider value={api}>
      {children}
      {stack.map((d) => <DialogRenderer key={d.id} dialog={d} onClose={(v) => close(d.id, v)} />)}
    </DialogContext.Provider>
  );
}

export const useDialog = () => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useDialog must be used inside <DialogProvider>");
  return ctx;
};

function DialogRenderer({ dialog, onClose }) {
  const {
    kind,
    title = "Are you sure?",
    description = "",
    confirmLabel,
    cancelLabel = "Cancel",
    destructive = false,
    placeholder = "",
    defaultValue = "",
    icon,
    options = [], // for "choose"
    showCreateOption = false, // for "choose"
    createOptionLabel = "Create new",
    actions = [], // for "actions": [{label, value, variant: "primary"|"secondary"|"destructive"}]
  } = dialog;

  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef(null);

  useEffect(() => {
    if (kind === "prompt" && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [kind]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (kind === "alert") onClose(true);
        else onClose(kind === "prompt" ? null : false);
      }
      if (e.key === "Enter" && kind !== "prompt") {
        // Enter confirms unless prompt (where Enter is captured by input)
        if (kind === "confirm") onClose(true);
        if (kind === "alert") onClose(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [kind, onClose]);

  const submitPrompt = () => {
    if (!value || !value.trim()) return;
    onClose(value.trim());
  };

  const cfm = confirmLabel || (kind === "alert" ? "OK" : kind === "confirm" ? (destructive ? "Confirm" : "OK") : "Save");

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 animate-fade-in"
      data-testid={`dialog-${kind}`}
      onClick={() => onClose(kind === "alert" ? true : (kind === "prompt" ? null : kind === "actions" ? null : false))}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md border border-[#1a1a1a] bg-[#0A0A0A] shadow-2xl"
      >
        <div className="flex items-start gap-4 p-6">
          <div className={`shrink-0 w-11 h-11 border flex items-center justify-center ${
            destructive ? "border-blood/40 bg-blood/10" : "border-[#262626] bg-[#1a1a1a]"
          }`}>
            {icon || (destructive ? <AlertTriangle className="text-blood" size={20} strokeWidth={1.75} /> : <Info className="text-[#F2EBE1]" size={20} strokeWidth={1.75} />)}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-xl font-bold uppercase tracking-tight">{title}</h2>
            {description && <p className="text-sm text-[#A19D94] mt-2 leading-relaxed">{description}</p>}
          </div>
        </div>

        {kind === "prompt" && (
          <div className="px-6 pb-4">
            <input
              ref={inputRef}
              data-testid="dialog-prompt-input"
              type="text"
              value={value}
              placeholder={placeholder}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitPrompt(); }}
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood"
            />
          </div>
        )}

        {kind === "choose" && (
          <div className="px-6 pb-4 space-y-1.5 max-h-64 overflow-y-auto">
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onClose({ kind: "select", value: opt.value })}
                data-testid={`dialog-choose-${opt.value}`}
                className="w-full text-left px-3 py-2.5 border border-[#262626] hover:border-blood hover:bg-blood/5 text-sm transition-colors"
              >
                <span className="text-[#F2EBE1]">{opt.label}</span>
                {opt.sublabel && <span className="block text-[10px] text-[#6B6862] font-mono uppercase tracking-wider mt-0.5">{opt.sublabel}</span>}
              </button>
            ))}
            {showCreateOption && (
              <button
                type="button"
                onClick={() => onClose({ kind: "create" })}
                data-testid="dialog-choose-create"
                className="w-full text-left px-3 py-2.5 border border-dashed border-blood text-blood hover:bg-blood/5 text-sm transition-colors font-display tracking-wider uppercase"
              >+ {createOptionLabel}</button>
            )}
            {options.length === 0 && !showCreateOption && <p className="text-sm text-[#6B6862] py-3">No options available.</p>}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#1a1a1a] bg-[#050505]">
          {kind === "actions" ? (
            <>
              <button
                type="button"
                onClick={() => onClose(null)}
                data-testid="dialog-cancel-btn"
                className="px-5 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-[#3a3835] hover:text-[#F2EBE1] transition-colors"
              >{cancelLabel}</button>
              {actions.map((a) => {
                const styles = {
                  primary: "bg-blood text-white hover:bg-[#FF3333]",
                  destructive: "bg-blood text-white hover:bg-[#FF3333]",
                  secondary: "border border-[#262626] text-[#A19D94] hover:border-blood hover:text-blood",
                }[a.variant || "primary"];
                return (
                  <button
                    key={a.value}
                    type="button"
                    onClick={() => onClose(a.value)}
                    data-testid={`dialog-action-${a.value}`}
                    className={`px-5 py-2 text-xs uppercase tracking-wider font-display transition-colors ${styles}`}
                  >{a.label}</button>
                );
              })}
            </>
          ) : (
            <>
              {kind !== "alert" && (
                <button
                  type="button"
                  onClick={() => onClose(kind === "prompt" ? null : false)}
                  data-testid="dialog-cancel-btn"
                  className="px-5 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-[#3a3835] hover:text-[#F2EBE1] transition-colors"
                >{cancelLabel}</button>
              )}
              {kind !== "choose" && (
                <button
                  type="button"
                  autoFocus={kind !== "prompt"}
                  onClick={kind === "prompt" ? submitPrompt : () => onClose(true)}
                  data-testid="dialog-confirm-btn"
                  disabled={kind === "prompt" && !value?.trim()}
                  className={`px-5 py-2 text-xs uppercase tracking-wider font-display transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    destructive
                      ? "bg-blood text-white hover:bg-[#FF3333]"
                      : "bg-blood text-white hover:bg-[#FF3333]"
                  }`}
                >{cfm}</button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
